import { useEffect, useState, type ComponentType } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { cn } from "@wa/ui";
import { logout, userCanPlatform, userCanPlatformAny, type StaffUser } from "../lib/auth";
import { CRM_ROUTES, isRouteActive } from "./nav";
import { Rail } from "./shell/Rail";
import { TopBar } from "./shell/TopBar";
import { useIsNarrow, useNavDrawer } from "./shell/navDrawer";
import { useRailCollapse } from "./shell/railCollapse";
import { CommandPalette } from "./shell/CommandPalette";
import { RouteErrorBoundary } from "./shell/ErrorBoundary";
import { CrmUserProvider } from "./shell/CrmUser";
import { SignalsProvider } from "./shell/SignalsProvider";
import { ToastHost } from "./ui/Toast";
import { DashboardSection } from "./sections/dashboard/DashboardSection";
import { MarketingSection } from "./sections/marketing";
import { AriregisterSection } from "./sections/ariregister/AriregisterSection";
import { LegacyRedirect, ProspectingSection } from "./sections/prospecting";
import { LeadsSection } from "./sections/leads/LeadsSection";
import { ClientsSection } from "./sections/clients/ClientsSection";
import { CalendarSection } from "./sections/calendar";
import { GuidesSection } from "./sections/guides";
import { SalesSection } from "./sections/sales/SalesSection";
import { ExpensesSection } from "./sections/expenses/ExpensesSection";
import { ProjectsSection } from "./sections/projects/ProjectsSection";
import { TasksSection } from "./sections/tasks/TasksSection";
import { ContractsSection } from "./sections/contracts/ContractsSection";
import { SupportSection } from "./sections/support/SupportSection";
import { VaultSection } from "./sections/vault/VaultSection";
import { MonitoringSection } from "./sections/monitoring";
import { EmailSection } from "./sections/email/EmailSection";
import { CallsSection } from "./sections/calls/CallsSection";
import { AiCallsSection } from "./sections/ai-calls";
import { ReportsSection } from "./sections/reports/ReportsSection";
import { ToolsSection } from "./sections/tools/ToolsSection";
import { SettingsSection } from "./sections/settings/SettingsSection";
import { MediaSection } from "./sections/media/MediaSection";
import { PlansSection } from "./sections/packages/PlansSection";
import { SubscriptionsSection } from "./sections/subscriptions/SubscriptionsSection";
import { CallProvider } from "./telephony/CallProvider";
import { TimerProvider } from "./timer";
import { SuppressedNumbersContext, useSuppressedNumbersState } from "./telephony/useSuppressedNumbers";
import { AiTestCallHost } from "./telephony/aiTestCall";
import { AiCallDock } from "./telephony/aiLive";

/** Section key (from CRM_ROUTES) to the component that owns that route tree. */
const SECTIONS: Record<string, ComponentType<{ user: StaffUser }>> = {
  calendar: CalendarSection,
  guides: GuidesSection,
  marketing: MarketingSection,
  ariregister: AriregisterSection,
  // The five prospect pages are tabs of this one now. Their old addresses each
  // keep a route that mounts the redirect, so a bookmark still lands.
  prospects: ProspectingSection,
  "prospects-legacy": LegacyRedirect,
  email: EmailSection,
  calls: CallsSection,
  "ai-calls": AiCallsSection,
  leads: LeadsSection,
  clients: ClientsSection,
  sales: SalesSection,
  expenses: ExpensesSection,
  projects: ProjectsSection,
  tasks: TasksSection,
  contracts: ContractsSection,
  support: SupportSection,
  monitoring: MonitoringSection,
  vault: VaultSection,
  reports: ReportsSection,
  tools: ToolsSection,
  plans: PlansSection,
  subscriptions: SubscriptionsSection,
  media: MediaSection,
  settings: SettingsSection,
};

/**
 * Screens that fill the content area instead of scrolling it: a folder rail, a
 * list and a reading pane, each scrolling on its own. They need the outer
 * column to stop scrolling, or the page would scroll behind three panes that
 * are already scrolling themselves.
 *
 * ⚠ Without this a page whose root asks for `h-full` gets no height to be 100%
 * OF. `.crm-page` is sized by its own content, so the map's shell measured
 * itself against a column its own filter rail had already made 2 000px tall,
 * and the whole page scrolled - map and all - instead of the rail scrolling
 * inside a fixed frame. `.crm-page.is-workspace` is what makes the height
 * definite; the rail's `min-h-0` further down was never the missing half.
 */
const WORKSPACE = ["/email", "/calls", "/ariregister/kaart"];

/**
 * The workspaces that ALSO collapse the nav rail on arrival.
 *
 * A subset rather than the same list, because the two are different arguments.
 * Mail and calls are already three columns wide and the nav labels are the
 * cheapest of them to give up. The map is two columns and has a fullscreen
 * button of its own, so collapsing the shell behind the operator's back would
 * be a surprise rather than a favour.
 */
const AUTO_COLLAPSE = ["/email", "/calls"];

export function CrmLayout({ user, onLogout }: { user: StaffUser; onLogout: () => void }) {
  const { pathname } = useLocation();
  const narrow = useIsNarrow();
  const routes = CRM_ROUTES.filter((r) => userCanPlatformAny(user, r.perm));
  const workspace = WORKSPACE.some((p) => isRouteActive(pathname, p));
  const [collapsed, toggleRail] = useRailCollapse({
    enabled: !narrow,
    auto: AUTO_COLLAPSE.some((p) => isRouteActive(pathname, p)),
    section: pathname.split("/")[1] ?? "",
  });
  const [drawer, setDrawer] = useNavDrawer(narrow, pathname);
  const [palette, setPalette] = useState(false);
  // Held for the whole shell so every printed number knows whether it is on the
  // do-not-call list. Fetched only for operators who can place calls at all.
  const suppressed = useSuppressedNumbersState(userCanPlatform(user, "use:crm.calls"));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function doLogout() {
    await logout().catch(() => {});
    onLogout();
  }

  return (
    // Outermost: who the operator is has to be settled before anything keyed on
    // them can start, and `SignalsProvider` is keyed on `user.id`.
    <CrmUserProvider user={user}>
      <SignalsProvider user={user}>
        <ToastHost>
          <SuppressedNumbersContext.Provider value={suppressed}>
            <CallProvider user={user}>
              {/* One AI test-call dialog for every screen that offers the call:
                  a menu entry closes its menu, so the dialog cannot live in it. */}
              <AiTestCallHost>
              {/* Above the header, because the running clock is drawn IN the
                  header and has to outlive every page under it. Inside the toast
                  host, because stopping one says how many hours it recorded. */}
              <TimerProvider user={user}>
                <div className="flex h-dvh flex-col">
                  <TopBar
                    user={user}
                    onLogout={doLogout}
                    onSearch={() => setPalette(true)}
                    menu={narrow ? { open: drawer, toggle: () => setDrawer(!drawer) } : undefined}
                  />

                  <div className="crm-body">
                    <Rail
                      user={user}
                      collapsed={collapsed}
                      onToggle={toggleRail}
                      open={drawer}
                      onNavigate={narrow ? () => setDrawer(false) : undefined}
                    />

                    {/* Only while the drawer is over the page. The scrim is what makes
                        a tap outside the menu mean "close" instead of landing on a
                        row of the page underneath. */}
                    {narrow ? (
                      <div
                        className={cn("crm-nav-scrim", drawer && "is-on")}
                        onClick={() => setDrawer(false)}
                        aria-hidden="true"
                      />
                    ) : null}

                    <main className={cn("crm-main", workspace && "is-workspace")}>
                      {/* Keyed on the route so the entrance animation replays on a
                          navigation rather than only on the first mount. On the
                          FIRST segment only, and that is load-bearing for pages
                          with routed tabs: every Kliendiotsing tab is
                          `prospects`, so a tab switch swaps the tab body without
                          remounting the page around it or replaying its
                          entrance. (A query change such as the scanner's
                          `?url=` never touches the key: it is not the path.) */}
                      <div key={pathname.split("/")[1] ?? ""} className={cn("crm-page", workspace && "is-workspace")}>
                        {/* Inside the routed area only: a section that throws must not
                            take the header and the rail with it, or the operator has
                            nowhere left to click. */}
                        <RouteErrorBoundary resetKey={pathname}>
                          <Routes>
                            <Route path="/" element={<DashboardSection user={user} />} />
                            {routes.map((r) => {
                              const Section = SECTIONS[r.section];
                              if (!Section) return null;
                              return (
                                <Route
                                  key={r.path}
                                  path={r.nested ? `${r.path}/*` : r.path}
                                  element={<Section user={user} />}
                                />
                              );
                            })}
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </RouteErrorBoundary>
                      </div>
                    </main>
                  </div>
                </div>

                {palette ? <CommandPalette user={user} onClose={() => setPalette(false)} /> : null}
                {/* The AI's calls as they happen, for everybody on the phone. Inside
                    CallProvider on purpose: "take over in this browser" puts the
                    operator on the call through the provider's own window (ring
                    R6d). Watching alone never touches it - the dock reads its own
                    stream, and the provider stays idle until somebody presses. */}
                {userCanPlatform(user, "use:crm.calls") ? (
                  <AiCallDock canManage={userCanPlatform(user, "manage:crm.calls")} />
                ) : null}
              </TimerProvider>
              </AiTestCallHost>
            </CallProvider>
          </SuppressedNumbersContext.Provider>
        </ToastHost>
      </SignalsProvider>
    </CrmUserProvider>
  );
}
